import { MultiLang } from '../types';

export const nav = {
  es: {
    home: 'Inicio',
    domotica: 'Servicios',
    electricista: 'Electricidad',
    projects: 'Proyectos',
    proceso: 'Cómo trabajamos',
    about: 'Nosotros',
    faq: 'FAQ',
    contact: 'Contacto',
  },
  en: {
    home: 'Home',
    domotica: 'Services',
    electricista: 'Electricity',
    projects: 'Projects',
    proceso: 'How we work',
    about: 'About',
    faq: 'FAQ',
    contact: 'Contact',
  },
  fr: {
    home: 'Accueil',
    domotica: 'Services',
    electricista: 'Électricité',
    projects: 'Projets',
    proceso: 'Notre méthode',
    about: 'À propos',
    faq: 'FAQ',
    contact: 'Contact',
  },
};

export const hero = {
  title: {
    es: 'Menos consumo. Más control.',
    en: 'Less energy. More control.',
    fr: 'Moins d\'énergie. Plus de contrôle.',
  } as MultiLang,
  subtitle: {
    es: 'Diseño eléctrico y automatización que reduce costes y mejora el confort.',
    en: 'Electrical design and automation that cuts costs and improves comfort.',
    fr: 'Conception électrique et automatisation qui réduit les coûts et améliore le confort.',
  } as MultiLang,
  cta: {
    es: 'Iniciar proyecto',
    en: 'Start your project',
    fr: 'Démarrer votre projet',
  } as MultiLang,
  cta2: {
    es: 'Ver cómo trabajamos',
    en: 'See how we work',
    fr: 'Voir notre méthode',
  } as MultiLang,
};

export const servicesSection = {
  label: {
    es: 'Lo que hacemos',
    en: 'What we do',
    fr: 'Ce que nous faisons',
  } as MultiLang,
  title: {
    es: 'Diseño integral. Sin compartimentos.',
    en: 'Integrated design. No silos.',
    fr: 'Conception intégrée. Sans cloisonnement.',
  } as MultiLang,
  items: [
    {
      id: 'domotica',
      emoji: '⚙️',
      title: { es: 'Automatización Inteligente', en: 'Intelligent Automation', fr: 'Automatisation Intelligente' } as MultiLang,
      description: {
        es: 'Diseñamos sistemas de automatización a medida — KNX, DALI, BACnet o soluciones híbridas. Un edificio inteligente no solo reacciona: ahorra energía, mejora el confort y se mantiene fácilmente. Definimos la arquitectura, asignamos lógicas de control y entregamos documentación técnica completa con copia de programación. Sin dependencia de plataforma única.',
        en: 'We design bespoke automation systems — KNX, DALI, BACnet or hybrid solutions. A smart building does not just react: it saves energy, improves comfort and stays easy to maintain. We define the architecture, assign control logic and deliver full technical documentation including a programming backup. No single-platform dependency.',
        fr: "Nous concevons des systèmes d'automatisation sur mesure — KNX, DALI, BACnet ou solutions hybrides. Un bâtiment intelligent ne réagit pas seulement : il économise l'énergie, améliore le confort et reste facile à maintenir. Architecture, logique de contrôle et documentation technique complète avec sauvegarde de programmation incluses.",
      } as MultiLang,
      image: 'https://images.unsplash.com/photo-1652715564391-38cc4475b7f5?auto=format&fit=crop&w=800&q=80',
    },
    {
      id: 'iluminacion',
      emoji: '💡',
      title: { es: 'Control de Iluminación', en: 'Lighting Control', fr: 'Contrôle Éclairage' } as MultiLang,
      description: {
        es: 'Regulación DALI y 0–10V por zonas con temperatura de color de 2700 K a 6500 K. Detección de presencia y regulación por luz natural — una combinación que puede reducir el consumo lumínico hasta un 30%. Escenas por horario u ocupación. Plano de zonas y guía de usuario incluidos en cada entrega.',
        en: 'DALI and 0–10V zone dimming with colour temperature from 2700 K to 6500 K. Presence detection and daylight regulation — a combination that can cut lighting consumption by up to 30%. Scenes by schedule or occupancy. Zone layout drawing and user guide included in every handover.',
        fr: "Gradation DALI et 0–10V par zones avec température de couleur de 2700 K à 6500 K. Détection de présence et régulation par lumière naturelle — une combinaison qui peut réduire la consommation d'éclairage jusqu'à 30%. Scènes par horaire ou occupation. Plan des zones et guide d'utilisation inclus à chaque livraison.",
      } as MultiLang,
      image: 'https://images.unsplash.com/photo-1763289926112-c39df1bd8b73?auto=format&fit=crop&w=800&q=80',
    },
    {
      id: 'persianas',
      emoji: '🪟',
      title: { es: 'Motorización y Climatización', en: 'Motorisation & Climate', fr: 'Motorisation & Climatisation' } as MultiLang,
      description: {
        es: 'Integración de persianas, toldos y climatización en un único sistema de control. Motores Somfy, Velux y Nice. Automatismos por posición solar, temperatura y viento. Gestión por zonas de calefacción, refrigeración y ventilación — todo documentado en el esquema de cuadro.',
        en: 'Integration of blinds, awnings and HVAC into a single control system. Somfy, Velux and Nice motors. Automation by solar position, temperature and wind. Zone-based management of heating, cooling and ventilation — all documented in the panel schematic.',
        fr: "Intégration volets, stores et climatisation dans un système de contrôle unique. Moteurs Somfy, Velux et Nice. Automatismes par position solaire, température et vent. Gestion par zones du chauffage, refroidissement et ventilation — tout documenté dans le schéma de tableau.",
      } as MultiLang,
      image: 'https://images.unsplash.com/photo-1759724141597-1bb021cc3823?auto=format&fit=crop&w=800&q=80',
    },
    {
      id: 'climatizacion',
      emoji: '🌡️',
      title: {
        es: 'Climatización Avanzada',
        en: 'Advanced Climate Control',
        fr: 'Climatisation Avancée',
      } as MultiLang,
      description: {
        es: 'Gestión inteligente de la temperatura por estancias. Integración con aerotermia, suelo radiante y sistemas de ventilación para un confort perfecto.',
        en: 'Smart temperature management by room. Integration with heat pumps, underfloor heating and ventilation systems for perfect comfort.',
        fr: 'Gestion intelligente de la température par pièce. Intégration pompe à chaleur, plancher chauffant et ventilation pour un confort parfait.',
      } as MultiLang,
      image: 'https://images.unsplash.com/photo-1631541909061-71e349d1f203?auto=format&fit=crop&w=800&q=80',
    },
    {
      id: 'electricidad',
      emoji: '⚡',
      title: {
        es: 'Diseño Eléctrico',
        en: 'Electrical Design',
        fr: 'Conception Électrique',
      } as MultiLang,
      description: {
        es: 'Diseño de la instalación eléctrica completa: cuadros de distribución, esquemas unifilares, cálculo de secciones y protecciones. Toda la documentación entregada en formato editable. Compatible y coordinada con el sistema de automatización desde el inicio del proyecto.',
        en: 'Complete electrical installation design: distribution boards, single-line diagrams, cross-section and protection calculations. All documentation delivered in editable format. Designed in coordination with the automation system from project inception.',
        fr: "Conception complète de l'installation électrique : tableaux de distribution, schémas unifilaires, calcul des sections et protections. Documentation complète livrée en format éditable. Conçue en coordination avec le système d'automatisation dès le début du projet.",
      } as MultiLang,
      image: 'https://images.unsplash.com/photo-1621905251189-276583892e38?auto=format&fit=crop&w=800&q=80',
    },
    {
      id: 'seguridad',
      emoji: '🔒',
      title: {
        es: 'Seguridad e Integración AV',
        en: 'Security & AV Integration',
        fr: 'Sécurité et Intégration AV',
      } as MultiLang,
      description: {
        es: 'Integración de cámaras IP, detección de intrusión, videoportero y sistemas de audio-vídeo distribuido. Todo centralizado en una única interfaz de control. Sin dependencia de plataformas externas, sin suscripciones obligatorias.',
        en: 'Integration of IP cameras, intrusion detection, video intercom and distributed audio-video systems. Everything centralised in a single control interface. No dependency on external platforms, no mandatory subscriptions.',
        fr: "Intégration caméras IP, détection d'intrusion, visiophone et systèmes audio-vidéo distribués. Tout centralisé dans une interface de contrôle unique. Sans dépendance à des plateformes externes, sans abonnements obligatoires.",
      } as MultiLang,
      image: 'https://images.unsplash.com/photo-1599770643145-3d8045e732e7?auto=format&fit=crop&w=800&q=80',
    },
  ],
};

export const aboutSection = {
  label: {
    es: 'Sobre nosotros',
    en: 'About us',
    fr: 'À propos',
  } as MultiLang,
  title: {
    es: 'Un estudio. Una metodología. Sin excepciones.',
    en: 'One studio. One methodology. No exceptions.',
    fr: "Un cabinet. Une méthodologie. Sans exception.",
  } as MultiLang,
  description: {
    es: 'Vatio Central es un estudio especializado en diseño eléctrico y automatización para proyectos residenciales y comerciales en España. Creemos que lo que no se mide no se puede mejorar — por eso cada proyecto incluye documentación técnica completa: planos as-built, esquemas, copia de programación y manual de usuario. Trabajamos con KNX, DALI, BACnet o soluciones integradas, siempre con proceso de 10 etapas sin excepción y sin dependencia de plataforma única.',
    en: 'Vatio Central is a studio specialising in electrical design and automation for residential and commercial projects in Spain. We believe what cannot be measured cannot be improved — which is why every project includes full technical documentation: as-built drawings, schematics, programming backup and user manual. We work with KNX, DALI, BACnet or integrated solutions, always with a 10-stage process and no single-platform dependency.',
    fr: "Vatio Central est un cabinet spécialisé en conception électrique et automatisation pour des projets résidentiels et commerciaux en Espagne. Ce qui ne se mesure pas ne peut pas s'améliorer — c'est pourquoi chaque projet inclut une documentation technique complète : plans as-built, schémas, sauvegarde de programmation et manuel utilisateur. Nous travaillons avec KNX, DALI, BACnet ou solutions intégrées, toujours avec un processus en 10 étapes et sans dépendance à une plateforme unique.",
  } as MultiLang,
  stats: [
    { value: 'KNX', label: { es: 'Partner certificado', en: 'Certified partner', fr: 'Partenaire certifié' } as MultiLang },
    { value: '10', label: { es: 'Etapas por proyecto', en: 'Stages per project', fr: 'Étapes par projet' } as MultiLang },
    { value: 'DOC', label: { es: 'Documentación completa siempre', en: 'Full documentation always', fr: 'Documentation complète toujours' } as MultiLang },
    { value: 'ES', label: { es: 'Toda la Península', en: 'All of Spain', fr: 'Toute la Péninsule' } as MultiLang },
  ],
};

export const contactSection = {
  label: {
    es: 'CONTACTO',
    en: 'CONTACT',
    fr: 'CONTACT',
  } as MultiLang,
  title: {
    es: 'Hablemos de tu proyecto.',
    en: "Let's talk about your project.",
    fr: 'Parlons de votre projet.',
  } as MultiLang,
  namePlaceholder: { es: 'Nombre', en: 'Name', fr: 'Nom' } as MultiLang,
  emailPlaceholder: { es: 'Email', en: 'Email', fr: 'Email' } as MultiLang,
  phonePlaceholder: { es: 'Teléfono', en: 'Phone', fr: 'Téléphone' } as MultiLang,
  messagePlaceholder: { es: 'Cuéntanos tu proyecto...', en: 'Tell us about your project...', fr: 'Parlez-nous de votre projet...' } as MultiLang,
  button: { es: 'Enviar Mensaje', en: 'Send Message', fr: 'Envoyer le Message' } as MultiLang,
};
